import useAnimatedPress from '@ui/animated/useAnimatedPress'
import { AnimatedPressable } from '@ui/animated/nativewindReanimated'
import { widthDefault } from '@ui/theme/values'
import { Deal as DealType } from '@ca/common/types'
import { getImageUrl } from 'config/getImageUrl'
import { BlurView } from 'expo-blur'
import { Image } from 'expo-image'
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native'
import { useAnimatedStyle } from 'react-native-reanimated'
import { getAspectRatio } from '@ui/utils/image'
import { colors } from '@ui/colors'
import Skeleton from '@ui/elements/Skeleton'
import Outline from '@ui/elements/Outline'

const isWeb = Platform.OS === 'web'

interface Props {
  deal: DealType | undefined
  onPress: () => void
  grid?: number
  gap?: number
  showTitle?: boolean
  isActive?: boolean
  index: number
}

export const useDealDimensions = (
  grid: number,
  gap: number,
  image?: {
    width?: number
    height?: number
  }
) => {
  const screen = useWindowDimensions()

  const sourceAspectRatio =
    getAspectRatio({
      width: image?.width,
      height: image?.height,
    }) || 1

  const isSquare = sourceAspectRatio === 1

  const widthResponsive = (screen.width - 2 * gap - gap * (grid - 1)) / grid
  const widthStatic = (widthDefault - 2 * gap - gap * (grid - 1)) / grid

  const widthOuter = Math.min(widthResponsive, widthStatic)

  const height = widthOuter
  const width = widthOuter

  const borderRadius = Platform.select({
    default: 24,
    web: 32,
  })

  return {
    widthOuter,
    width,
    height,
    borderRadius,
    isSquare,
  }
}

export default function Deal({
  deal,
  onPress,
  grid = 2,
  gap = 32,
  isActive,
}: Props) {
  const { animation, onPressIn, onPressOut } = useAnimatedPress()

  const scaleStyle = useAnimatedStyle(() => {
    return {
      transform: [{ scale: 1 - animation.value * 0.07 }],
    }
  })

  const { width, widthOuter, height, borderRadius, isSquare } =
    useDealDimensions(grid, gap, deal?.image)

  const outlineSize = 4

  const imageSrc = getImageUrl(deal?.image?.url)
  const dimensions = {
    width,
    height,
  }

  return (
    <View
      className="relative flex flex-col"
      style={{
        width: widthOuter,
        height,
        alignItems: 'center',
      }}
    >
      <Skeleton
        className="absolute left-0 top-0"
        style={{
          width: widthOuter,
          height: height,
          borderRadius,
          backgroundColor: colors.black10,
        }}
      />
      <AnimatedPressable
        style={[
          dimensions,
          scaleStyle,
          {
            flex: 1,
            // transformOrigin: transformOrigin[index % 4]
          },
        ]}
        onPress={onPress}
        className="flex-col items-center"
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        disabled={!deal}
      >
        {!isSquare && (
          <View
            className="overflow-hidden-fixer absolute left-0 top-0 h-full w-full overflow-hidden"
            style={{ borderRadius }}
          >
            <Image source={imageSrc} contentFit="cover" style={dimensions} />
            {!isWeb && (
              <BlurView
                style={StyleSheet.absoluteFill}
                intensity={75}
                tint="systemUltraThinMaterialDark"
              />
            )}
            {isWeb && (
              <div className="absolute left-0 top-0 h-full w-full backdrop-blur-3xl" />
            )}
          </View>
        )}
        {isActive && (
          <Outline
            size={outlineSize}
            borderRadius={borderRadius - outlineSize / 2}
          />
        )}
        <Image
          source={imageSrc}
          style={[dimensions, { borderRadius }]}
          contentFit="contain"
          transition={500}
        />
      </AnimatedPressable>
    </View>
  )
}

export function DealPlaceholder({
  grid = 2,
  gap = 32,
}: Omit<Props, 'onPress' | 'deal'>) {
  const { width, height, borderRadius } = useDealDimensions(grid, gap)

  return (
    <View className="flex flex-col" style={{ width, height, borderRadius }} />
  )
}

const transformOrigin = ['bottom right', 'bottom left', 'top right', 'top left']
